import {
    Component,
    Input,
    ChangeDetectionStrategy,
    OnInit,
    ChangeDetectorRef,
    signal,
    ViewEncapsulation,
    ElementRef,
    ViewChild,
    Output,
    EventEmitter,
} from "@angular/core";
import { DropDownItem } from "../../pojo/DropDownItem";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemContactComponent } from "../s25-item/item-object/s25.item.contact.component";
import { ContactRole, UpdatedContactRoles } from "../../services/organization.service";
import { Table } from "../s25-table/Table";
import Unpaginated = Table.Unpaginated;
import { S25DropdownSearchCriteriaComponent } from "../s25-dropdown/single-select/s25.dropdown.search.criteria.component";
import Column = Table.Column;
import { S25RadioComponent } from "../s25-radio/s25.radio.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { Bind } from "../../decorators/bind.decorator";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { S25WsMicroService } from "../../services/MicroServices/S25WsMicroService";
import { S25WsMicroOrganization } from "../../services/MicroServices/S25WsMicroOrganization";

@TypeManagerDecorator("s25-ng-org-contact-roles")
@Component({
    selector: "s25-ng-org-contact-roles",
    template: `
        <s25-ng-table
            class="c-margin-right--half c-margin-left--half c-margin-top--half"
            [dataSource]="tableConfig"
            [columnSortable]="false"
            [noResultsMessage]="'No Associated Contacts'"
            [pivotThreshold]="300"
        >
        </s25-ng-table>

        @if (message()) {
            <div class="ngRed">{{ message() }}</div>
        }
        @if (canEdit) {
            <s25-ng-dropdown-search-criteria
                class="c-margin-bottom--half"
                [type]="'contacts'"
                [placeholder]="'Add a Contact'"
                (chosenChange)="onAdd($event)"
                noSelectionUsePlaceholder="true"
            ></s25-ng-dropdown-search-criteria>
        }
    `,
    styles: `
        ::ng-deep s25-ng-table thead {
            background-color: var(--base-background) !important;
        }

        ::ng-deep s25-ng-table thead th .headerText {
            font-weight: bold;
        }

        ::ng-deep s25-item-contact {
            .s25-item-holder {
                display: flex;
                align-items: center;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25OrgContactRolesComponent implements OnInit {
    @Input() itemId: number; //OrganizationId
    @Input() contacts: ContactRole[] = [];
    @Input() canEdit = false;
    @Input() onChange: (
        primaryId: number,
        changed: UpdatedContactRoles,
    ) => Promise<S25WsMicroService.Content<S25WsMicroOrganization.Content>>;
    @Output() changes = new EventEmitter<UpdatedContactRoles>();

    @ViewChild(S25TableComponent) table: S25TableComponent;

    init: boolean = false;
    primary: number;
    tableConfig: Unpaginated;
    changed: UpdatedContactRoles = { added: [], removed: [], updated: [] };
    message = signal<string>("");

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    /**
     * Configure the table columns based on whether the user can edit or not.
     */
    configureTable() {
        let columns: Column[] = [];

        if (this.canEdit) {
            columns = [
                {
                    header: "Remove",
                    id: "remove",
                    align: "center",
                    maxWidth: 35,
                    content: {
                        component: S25IconButtonComponent,
                        inputs: {
                            type: "close",
                            label: "Remove",
                        },
                        outputs: { click: this.onRemove },
                    },
                },
                {
                    header: "Primary",
                    id: "primary",
                    sortable: true,
                    align: "center",
                    maxWidth: 35,
                    content: {
                        component: S25RadioComponent,
                        outputs: { modelValueChange: this.setPrimary },
                    },
                },
                {
                    header: "Contact",
                    id: "contact",
                    sortable: true,
                    align: "left",
                    content: {
                        component: S25ItemContactComponent,
                    },
                },
                {
                    header: "Role",
                    id: "role",
                    sortable: true,
                    align: "center",
                    content: {
                        component: S25DropdownSearchCriteriaComponent,
                        outputs: { chosenChange: this.changeRole },
                    },
                },
            ];
        } else {
            columns = [
                {
                    header: "Contact",
                    id: "contact",
                    sortable: true,
                    content: {
                        component: S25ItemContactComponent,
                    },
                    align: "left",
                },
                { id: "role", header: "Role", align: "center" },
                {
                    header: "Primary",
                    id: "primary",
                    sortable: true,
                    align: "center",
                },
            ];
        }

        this.tableConfig = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: columns,
        };
    }

    getRows = () => {
        const rows: Table.Row[] = this.contacts?.map(this.mapToRow) ?? [];
        return Promise.resolve({ rows: rows });
    };

    @Bind
    mapToRow(contact: ContactRole): Table.Row {
        let retVal: Table.Row = {
            id: contact.itemId,
            name: contact.itemName,
            data: contact,
            cells: {},
        };

        if (this.canEdit) {
            retVal.cells = {
                remove: {
                    className: "s25-remove-button",
                    inputs: { type: "close", label: "Remove" },
                },
                primary: { inputs: { modelValue: this.primary, value: contact.itemId, name: "primaryContact" } },
                contact: {
                    className: "s25-item-name",
                    inputs: { itemId: contact.itemId, itemName: contact.itemName, includeTypeIcon: true },
                },
                role: {
                    inputs: {
                        type: "organizationRoles",
                        chosen: contact.role,
                        placeholder: "Choose a Role",
                    },
                },
            };
        } else {
            retVal.cells = {
                contact: {
                    className: "s25-item-name",
                    inputs: { itemId: contact.itemId, itemName: contact.itemName, includeTypeIcon: true },
                },
                role: { text: contact.role?.itemName || "" },
                primary: { text: contact.isPrimary ? "Yes" : "No" },
            };
        }

        return retVal;
    }

    ngOnInit(): void {
        this.primary = this.contacts?.find((c) => c.isPrimary)?.itemId || null;
        this.configureTable();
        this.init = true;
        this.cd.detectChanges();
    }

    @Bind
    onAdd(newItem: DropDownItem) {
        // Check if the item already exists in the rows
        const existingIndex = this.table.rows.findIndex((row) => row.data.itemId === newItem.itemId);
        if (existingIndex > -1) {
            this.message.set("Contact already exists in the list.");
            return;
        }

        if (!this.primary) newItem.isPrimary = true;

        const newRow = this.mapToRow(newItem as ContactRole);

        this.table.addRow(newRow);
        this.changed.added.push(newRow.data);
        this.outputChanges();
    }

    @Bind
    onRemove(item: any, row: Table.Row, instance: S25TableComponent) {
        this.table.removeRow(row);

        //remove previous changes
        this.changed.added = this.changed.added.filter((added) => added.itemId !== item.data.itemId);
        this.changed.updated = this.changed.updated.filter((updated) => updated.itemId !== item.data.itemId);

        this.changed.removed.push(row.data);
        this.outputChanges();
    }

    @Bind
    changeRole(item: DropDownItem, row: Table.Row) {
        row.data.role = { itemId: item.itemId, itemName: item.itemName };
        this.changed.updated.push(row.data);
        this.outputChanges();
    }

    @Bind
    async setPrimary(contId: number) {
        //for consistency update the primary contact on the original data
        const oldPrimary = this.table.rows.find((row) => row.data.isPrimary);
        const newPrimary = this.table.rows.find((row) => row.data.itemId === contId);
        if (oldPrimary) oldPrimary.data.isPrimary = false;
        newPrimary.data.isPrimary = true;

        this.outputChanges(contId);
    }

    async outputChanges(primaryId?: number) {
        this.message.set("");

        //make sure there are no duplicates between this.changed objects
        //Added takes priority over changed and removed
        this.changed.removed = this.changed.removed.filter(
            (removed) => !this.changed.added.some((added) => added.itemId === removed.itemId),
        );
        this.changed.updated = this.changed.updated.filter(
            (updated) => !this.changed.added.some((added) => added.itemId === updated.itemId),
        );

        this.changed.primaryId = primaryId;

        const [resp, err] = await S25Util.Maybe(this.onChange && this.onChange(primaryId, this.changed));
        if (err) {
            this.message.set(err);
        }
        this.changes.emit(this.changed);
    }
}
